import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { qk } from "@/lib/query-keys";
import { QmFrictionSchema, type QmFriction } from "@/lib/validation";

export interface QmFrictionParams {
  /** ISO-8601 datetime with offset, e.g. 2026-05-19T00:00:00Z. Required. */
  from: string;
  /** ISO-8601 datetime with offset, e.g. 2026-06-11T23:59:59Z. Required. */
  to: string;
  /** Max ranked pages returned (1..100). Server default: 20. */
  limit?: number;
  /** Pages below this pageview count are dropped as noisy (>=0). Server default: 50. */
  minPageviews?: number;
  /** Path-prefix filter, e.g. /account. */
  path?: string;
}

/**
 * Builds the QM friction querystring. Unlike the /metrics/* endpoints this has
 * its own param set (no granularity/env), so it does not reuse `toSearchParams`.
 * Optional params are omitted when unset so the server defaults apply.
 */
function toQmSearchParams(params: QmFrictionParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set("from", params.from);
  sp.set("to", params.to);
  if (params.limit !== undefined) sp.set("limit", String(params.limit));
  if (params.minPageviews !== undefined) {
    sp.set("minPageviews", String(params.minPageviews));
  }
  if (params.path) sp.set("path", params.path);
  return sp;
}

export interface UseQmFrictionOptions {
  enabled?: boolean;
}

/**
 * Use Case A1 — ranked "most frustrating pages" from QuantumMetric session data
 * (rage clicks, JS-error clicks, frustration, rage scrolls, disabled-input
 * clicks), normalized by pageviews. Backed by `GET /api/v1/qm/friction`.
 */
export function useQmFriction(
  params: QmFrictionParams,
  options: UseQmFrictionOptions = {},
) {
  return useQuery<QmFriction>({
    queryKey: qk.qm.friction(params),
    queryFn: async ({ signal }) => {
      const raw = await api.get<unknown>(
        `/api/v1/qm/friction?${toQmSearchParams(params).toString()}`,
        { signal },
      );
      return QmFrictionSchema.parse(raw);
    },
    enabled: options.enabled !== false,
    staleTime: 60_000,
  });
}
